import React, { MouseEventHandler, useMemo } from "react";
import { useHideOverflowing } from "../common/useHideOverflowing";
import { Destination, DestinationType, Organization, ProviderType, SourceWithRoutes } from "../common/ObjectTypes";
import { CopyToClipboard, RefreshSpinner } from "../common/ProjectIcons";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { page_size } from "../../api/apiUtils";
import { useTranslation } from "react-i18next";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "./SourceHelperFunctions";
import { tableClasses, listPageClasses, paginationClasses, filterClasses, skeletonClasses, buttonClasses } from "../common/tailwindClassnames";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";
import SortByDropdown, { SortOption } from "../common/SortByDropdown";

// Orderings offered by the Sort By dropdown — SourcesPage validates persisted
// filter state against this list (old column-header sorts saved values like
// integration__id that the dropdown doesn't offer).
export const sourceSortValues = ["external_id", "-external_id", "-created_at", "created_at"];

type SourcesListProps = {
    sources: SourceWithRoutes[],
    totalCount: number,
    take: number,
    offset: number,
    onPageClicked: MouseEventHandler,
    loading: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    globalSearchStr: string,
    onChangeGlobalSearch: (value: string) => void,
    onRefresh: () => void,
    onChangeOwner: Function,
    ownerFilterValue: any,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    ordering: string,
    onChangeSortBy: (selected: SortOption) => void,
    onViewDetails: Function,
    onViewRouteDetails: Function,
}

const SourcesList = ({
    sources,
    totalCount,
    take,
    offset,
    onPageClicked,
    loading,
    organizations,
    loadingOrganizations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    providerTypes,
    loadingProviderTypes,
    globalSearchStr,
    onChangeGlobalSearch,
    onRefresh,
    onChangeOwner,
    ownerFilterValue,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    ordering,
    onChangeSortBy,
    onViewDetails,
    onViewRouteDetails,
}: SourcesListProps) => {
    // Phones: a control that does not fully fit in its row is hidden, not cut in half.
    const searchRowRef = useHideOverflowing<HTMLDivElement>();
    const filterRowRef = useHideOverflowing<HTMLDivElement>();
    const { t } = useTranslation();
    const { timezone } = useTimezone();

    const sortOptions: SortOption[] = useMemo(() => [
        { label: t("External ID (A-Z)"), value: "external_id" },
        { label: t("External ID (Z-A)"), value: "-external_id" },
        { label: t("Newest First"), value: "-created_at" },
        { label: t("Oldest First"), value: "created_at" },
    ], [t]);

    const hasActiveFilters = !!(globalSearchStr || endpointFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeEndpoint(null);
        onChangeOwner(null);
        onChangeProviderType(null);
        onChangeDestinationType(null);
    };

    return (
        <div className="transition-all duration-300">
            <div className={listPageClasses.band}>
                <div className={listPageClasses.bandRow}>
                    <h1 data-testid="sources_page_header" className={listPageClasses.title}>
                        {t("Sources")}
                    </h1>
                </div>
            </div>
            <div className={listPageClasses.body}>
            <div className="max-w-list-table mx-auto"><div className="w-full">
            {/* Search row */}
            <div ref={searchRowRef} className="flex flex-row flex-nowrap items-end gap-3 mb-3 overflow-x-clip">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} savedString={globalSearchStr} />
                <SortByDropdown options={sortOptions} ordering={ordering} onChangeSortBy={onChangeSortBy} />
                <button
                    type="button"
                    onClick={() => onRefresh()}
                    aria-label={t("Refresh list")}
                    disabled={loading}
                    className={`flex justify-center items-center w-8 h-[38px] cursor-pointer self-end mb-2 ml-auto ${loading ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                >
                    <RefreshSpinner />
                </button>
                {hasActiveFilters && (
                    <button className={`${buttonClasses.clearFilters} whitespace-nowrap mb-2`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
            </div>
            {/* Filter dropdowns row */}
            <div ref={filterRowRef} className="flex flex-row gap-3 mb-3 flex-nowrap overflow-x-clip">
                {/* Destination */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-full"
                        variant="routeFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-full"
                        variant="routeFilter"
                    />
                </div>
                {/* Provider Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Provider Type")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className="w-full"
                        variant="routeFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1 flex-1 min-w-[8rem]">
                    <span className={filterClasses.label}>{t("Destination Type")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-full"
                        variant="routeFilter"
                    />
                </div>
            </div>
            {/* Table */}
            <div className="flex flex-col w-full">
                <table className={tableClasses.fixedContainer}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={`${tableClasses.headerCell} w-[28%]`}>
                                {t("External ID")}
                            </th>
                            <th className={`${tableClasses.headerCell} w-[25%]`}>
                                {t("Route")}
                            </th>
                            <th className={`${tableClasses.headerCell} w-[25%]`}>
                                {t("Destinations")}
                            </th>
                            <th className={`${tableClasses.headerCell} w-[22%]`}>
                                {t("Created At")}
                            </th>
                        </tr>
                    </thead>
                    {loading ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className={tableClasses.row} data-testid="loading">
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                            : <tbody className="text-sm">
                                {sources.map((src: any) => {
                                    return (
                                        <tr key={src.source.id}
                                            className={tableClasses.row}>
                                            <td className={tableClasses.cell}>
                                                <span className="block truncate" title={src.source.external_id}>
                                                    {src.source.external_id}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                <span
                                                    className="block truncate text-brand-accent-green transition-colors duration-300 cursor-pointer hover:underline"
                                                    title={src.source.provider.name}
                                                    onClick={() => onViewRouteDetails(src.source.provider.id)}
                                                >
                                                    {src.source.provider.name}
                                                </span>
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {src.source.destinations.map((dest: Destination) => {
                                                    let dest_url: string | URL;
                                                    try {
                                                        dest_url = new URL(dest.base_url).hostname
                                                    } catch (err) {
                                                        dest_url = ""
                                                    }
                                                    return (
                                                        <div key={dest.id} className="flex items-center gap-2 min-w-0">
                                                            <span className="block truncate text-brand-accent-green transition-colors duration-300 cursor-pointer hover:underline" title={String(dest_url)} onClick={() => onViewDetails(dest.id)}>{dest_url}</span>
                                                            {dest.base_url && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigator.clipboard.writeText(dest.base_url);
                                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                            position: toast.POSITION.TOP_RIGHT,
                                                                            autoClose: 2000
                                                                        });
                                                                    }}
                                                                    className="flex items-center justify-center flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                                                    aria-label={t("Copy to clipboard")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                <span className="block truncate">{formatSourceDatetime(src.source.created_at, timezone)}</span>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>}
                        {!loading && sources.length === 0 &&
                            <tbody><tr><td colSpan={4} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={`${t("You may need to adjust your search or filters to find what you are looking for")}.`}
                                />
                            </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    initialPage={Math.floor(offset / take)}
                    totalCount={totalCount}
                    take={take}
                    loading={loading}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div></div>
            </div>
        </div>
    )
};

export default SourcesList;
